import type { PlatformRole } from '@lb/domain'

/**
 * Navigation, per role — the ONLY place role branches (§1).
 *
 * Three sidebars and three bottom-tab sets, taken directly from the prototypes'
 * `NAV_ITEMS`, `PRO_NAV` and `TSP_NAV`. Everything downstream reads this, so a
 * role difference is a row in a table rather than an `if` in a component.
 *
 * Order matters and is the design's, not ours. Note TSP puts Cohort Journey
 * ABOVE My Journey — the cohort is the primary one for that role.
 */

export interface NavItem {
  /** Matches the route path, so the active state needs no lookup table. */
  to: string
  /** i18n key under `nav.`. Copy is never inline (§11). */
  labelKey: string
  icon: NavIcon
}

export type NavIcon =
  | 'home'
  | 'boat'
  | 'star4'
  | 'book'
  | 'headphones'
  | 'message'
  | 'compass'
  | 'calendar'
  | 'community'
  | 'robot'

/*
 * 18 Sep 2026 (operator): Balados left the nav — the episodes are a segment of
 * the Library now — and practitioners' Resources is the same Library a parent
 * gets, so its label is the same word.
 */
const PARENT_NAV: NavItem[] = [
  { to: '/', labelKey: 'home', icon: 'home' },
  { to: '/journey', labelKey: 'journey', icon: 'boat' },
  { to: '/resources', labelKey: 'resources', icon: 'book' },
  { to: '/qanda', labelKey: 'qanda', icon: 'message' },
  { to: '/events', labelKey: 'events', icon: 'calendar' },
  { to: '/community', labelKey: 'community', icon: 'community' },
  { to: '/challenge', labelKey: 'challenge', icon: 'star4' },
  { to: '/search', labelKey: 'coach', icon: 'robot' },
]

const PRO_NAV: NavItem[] = [
  { to: '/', labelKey: 'home', icon: 'home' },
  { to: '/journey', labelKey: 'journey', icon: 'boat' },
  { to: '/resources', labelKey: 'resources', icon: 'book' },
  { to: '/cases', labelKey: 'caseStudies', icon: 'compass' },
  { to: '/events', labelKey: 'events', icon: 'calendar' },
  { to: '/community', labelKey: 'community', icon: 'community' },
  { to: '/search', labelKey: 'coach', icon: 'robot' },
]

const TSP_NAV: NavItem[] = [
  { to: '/', labelKey: 'home', icon: 'home' },
  /*
   * ONE journey for TSP — the cohort's. Operator rule (21 Aug 2026): the
   * voyage (« My Journey ») belongs to the Pro role a member graduates into
   * when the cohort closes; while enrolled, the programme IS their journey.
   */
  { to: '/cohort', labelKey: 'cohortJourney', icon: 'star4' },
  { to: '/resources', labelKey: 'resources', icon: 'book' },
  { to: '/cases', labelKey: 'caseStudies', icon: 'compass' },
  { to: '/events', labelKey: 'events', icon: 'calendar' },
  { to: '/community', labelKey: 'community', icon: 'community' },
  { to: '/search', labelKey: 'coach', icon: 'robot' },
]

/**
 * A full Record, so adding a platform role fails to compile until someone
 * decides what it sees — the same discipline the backend's mediators use.
 *
 * ORG_ADMIN: answered 2026-08-01 — an org admin IS a content consumer. The
 * `ORG_ADMIN → ORG` mapping is applied on six backend surfaces (content,
 * community, search, library, announcements, onboarding), so it gets a full
 * shell rather than an administrative one, plus the seat panel.
 *
 * With one measured caveat worth keeping here so nobody hunts it as a bug:
 * **no content anywhere carries the ORG audience** — the backend counted
 * `PRO 2458 · PARENT 846 · TSP 91 · ORG 0`. An org admin's Resources reads
 * `total: 0` against a parent's 282. That is an unticked checkbox in the
 * audience picker, not a routing fault, and whether ORG should be a content
 * pool at all is with the operator.
 */
export const NAV_BY_ROLE: Record<PlatformRole, NavItem[]> = {
  PARENT: PARENT_NAV,
  PRO: PRO_NAV,
  TSP: TSP_NAV,
  ORG_ADMIN: PRO_NAV,
}

/**
 * The mobile bottom bar: four items, a subset of that role's nav, in nav order.
 * From the prototypes' `BN_PRIMARY`, `PRO_BN` and `TSP_BN`.
 */
const BOTTOM_TABS: Record<PlatformRole, string[]> = {
  PARENT: ['/', '/journey', '/community', '/resources'],
  PRO: ['/', '/journey', '/resources', '/cases'],
  TSP: ['/', '/cohort', '/resources', '/cases'],
  ORG_ADMIN: ['/', '/journey', '/resources', '/cases'],
}

/**
 * Paths a member cannot open YET, and which the nav therefore does not offer.
 *
 * Not a role rule — those are the tables above. This is a TSP cohort member
 * before the étape that opens a surface (`journey.lockedSurfaces`, TSP request
 * 6): « Études de cas » opens at étape 3. The API names surfaces, the nav
 * names paths, and this is where one becomes the other.
 */
export const PATH_FOR_SURFACE: Record<string, string> = {
  caseStudies: '/cases',
}

/* ─────────────────────────────────────────────────────────────────────────
 * What a single-purchase member can reach (13 Oct 2026, operator).
 *
 * A purchase unlocks **one item**. The buyer keeps `entitled: false`, so every
 * other surface is still the subscription's — and until today the nav offered
 * all of them as if they were not, which reads as a product that half works.
 * The decision: the nav says plainly what the subscription buys, with a lock
 * on the rest.
 *
 * ── Two paths are conditional, and the condition is what they BOUGHT ──────
 * `/resources` is open to somebody who owns a course and `/events` to somebody
 * who owns a ticket, because that is where the thing they paid for lives. The
 * rest is the subscription, whole, with nothing a single purchase can open.
 *
 * ── Home and Mon voyage are never locked ─────────────────────────────────
 * Home is where « Vos achats » is — locking it would hide the purchase from
 * the person who made it — and the voyage is their own progress on their own
 * course, which nobody sold them.
 *
 * ── This is a NAV rule, not an access rule ───────────────────────────────
 * The server gates per row (`locked`) and answers 200 on every one of these
 * surfaces. So this decides what the rail OFFERS, never what the member may
 * read: a locked item still routes somewhere (`/plans`), and a direct URL
 * still renders whatever the API gives them. Nothing here re-locks a row the
 * server opened — the trap the single-purchase round exists to avoid.
 * ───────────────────────────────────────────────────────────────────────── */

/** Open to everybody, whatever they hold. */
const ALWAYS_OPEN: readonly string[] = ['/', '/journey']

/** Open only to somebody who owns one of these. */
const OPENED_BY_PURCHASE: Record<'COURSE' | 'EVENT', string> = {
  COURSE: '/resources',
  EVENT: '/events',
}

/**
 * The nav paths a single-purchase member cannot open, given what they own.
 *
 * `owns` is the set of content types they hold — `COURSE`, `EVENT`, or both.
 * Pure, and takes the role's own nav, so a path that is not in their rail is
 * never named.
 */
export function lockedNavPaths(
  role: PlatformRole,
  owns: ReadonlySet<'COURSE' | 'EVENT'>,
): ReadonlySet<string> {
  const open = new Set<string>(ALWAYS_OPEN)
  for (const type of owns) open.add(OPENED_BY_PURCHASE[type])

  return new Set(NAV_BY_ROLE[role].map((item) => item.to).filter((to) => !open.has(to)))
}

const NO_HIDDEN: ReadonlySet<string> = new Set()

/**
 * What a staff member sees on top of their role's nav (8 Oct 2026): every
 * section of all three portals — the parents' Q&A and weekly challenge, the
 * practitioners' Case Studies, the TSP cohort journey — so anybody with
 * back-office access sees what any member sees. Merged into one list in this
 * order, the designs' orders interleaved.
 */
const STAFF_EXTRAS: NavItem[] = [
  { to: '/journey', labelKey: 'journey', icon: 'boat' },
  { to: '/cohort', labelKey: 'cohortJourney', icon: 'star4' },
  { to: '/qanda', labelKey: 'qanda', icon: 'message' },
  { to: '/cases', labelKey: 'caseStudies', icon: 'compass' },
  { to: '/challenge', labelKey: 'challenge', icon: 'star4' },
]

const STAFF_ORDER = ['/', '/journey', '/cohort', '/resources', '/qanda', '/cases', '/events', '/community', '/challenge', '/search']

/** The sidebar for this role, without what is locked for this member. */
export function navFor(
  role: PlatformRole,
  hidden: ReadonlySet<string> = NO_HIDDEN,
  staff = false,
): NavItem[] {
  const base = NAV_BY_ROLE[role]
  const items = staff ? withStaffExtras(base) : base
  return items.filter((item) => !hidden.has(item.to))
}

function withStaffExtras(base: NavItem[]): NavItem[] {
  const missing = STAFF_EXTRAS.filter((extra) => !base.some((item) => item.to === extra.to))
  if (missing.length === 0) return base
  const rank = (to: string) => {
    const at = STAFF_ORDER.indexOf(to)
    return at === -1 ? STAFF_ORDER.length : at
  }
  return [...base, ...missing].sort((a, b) => rank(a.to) - rank(b.to))
}

/**
 * Four tabs, always. When one of the role's four is hidden, the next item in
 * nav order takes its slot rather than leaving a three-tab bar with a gap in
 * it — and it moves out of the More sheet, which is computed from this.
 */
export function bottomTabsFor(
  role: PlatformRole,
  hidden: ReadonlySet<string> = NO_HIDDEN,
  staff = false,
): NavItem[] {
  const nav = navFor(role, hidden, staff)
  const wanted = BOTTOM_TABS[role]
  const tabs = wanted
    .map((to) => nav.find((item) => item.to === to))
    .filter((item): item is NavItem => item !== undefined)
  for (const item of nav) {
    if (tabs.length >= wanted.length) break
    if (!tabs.includes(item)) tabs.push(item)
  }
  return tabs
}

/** Everything not on the bottom bar goes in the More sheet. */
export function moreItemsFor(
  role: PlatformRole,
  hidden: ReadonlySet<string> = NO_HIDDEN,
  staff = false,
): NavItem[] {
  const onBar = new Set(bottomTabsFor(role, hidden, staff).map((item) => item.to))
  return navFor(role, hidden, staff).filter((item) => !onBar.has(item.to))
}

/**
 * What this surface is called, in the two places that name it.
 *
 * `chip` is the badge beside the wordmark, lowercase because the design sets it
 * that way (`pro-brand-tag`). `label` is the same surface written into a
 * sentence — the profile pill's `Pro · Org licence`, where a lowercase `pro`
 * would read as a typo.
 *
 * Parent has neither: it is the default product, and a chip reading "parent"
 * on it would be labelling the absence of a distinction. The pill falls back to
 * the design's parent form (`Annual · Parent`) when there is no tag, which is
 * the same rule read from the other end — the leading slot says *which
 * product*, and with only one product it says which role instead.
 *
 * Here rather than in `app-shell` because the pill needs it too and the pill is
 * a child of the shell; two copies of "what PRO is called" is exactly the drift
 * this file exists to prevent.
 */
export const SURFACE: Partial<Record<PlatformRole, { chip: string; label: string }>> = {
  PRO: { chip: 'pro', label: 'Pro' },
  TSP: { chip: 'tsp', label: 'TSP' },
  ORG_ADMIN: { chip: 'org', label: 'Pro' },
}
